import { t as uiText } from '../../i18n';
export const MIN_WORKSPACE_ID_LENGTH = 6;
export const MAX_WORKSPACE_ID_LENGTH = 48;
export const MAX_WORKSPACE_NAME_DISPLAY_LENGTH = 48;
export const MIN_DISPLAY_NAME_LENGTH = 2;
export const MAX_DISPLAY_NAME_LENGTH = 20;

export const NCG_APPLY_PATH = '/sys/ncg-apply';
export const NCG_APPLY_PENDING_KEY = 'ncgApplyPending';

export const RESERVED_PUBLISH_IDS = [
  'admin',
  'api',
  'www',
  'mail',
  'ftp',
  'localhost',
  'test',
  'staging',
  'dev',
  'demo',
];

export const RESERVED_WORKSPACE_IDS = [
  'admin',
  'home',
  'open',
  'demo',
  'sys',
  'cg',
  'widget',
  'p',
];

export function getDisplayLength(text: string): number {
  let length = 0;
  for (const char of text) {
    length += char.charCodeAt(0) > 127 ? 2 : 1;
  }
  return length;
}

export function sanitizeWorkspaceId(input: string): string {
  return input.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
}

function sliceByDisplayLength(text: string, max: number): string {
  let length = 0;
  let result = '';
  for (const char of text) {
    const next = length + (char.charCodeAt(0) > 127 ? 2 : 1);
    if (next > max) break;
    result += char;
    length = next;
  }
  return result;
}

/** Google / メールの表示名から、公開AI用ワークスペース名の初期値を作る。 */
export function suggestWorkspaceName(displayName: string | null | undefined): string {
  const trimmed = (displayName || '').trim();
  if (!trimmed) return '';
  const suffix = uiText(" Public AI");
  const max = MAX_WORKSPACE_NAME_DISPLAY_LENGTH;
  const candidate = `${trimmed}${suffix}`;
  if (getDisplayLength(candidate) <= max) return candidate;
  const nameBudget = max - getDisplayLength(suffix);
  if (nameBudget < 1) return sliceByDisplayLength(trimmed, max);
  const sliced = sliceByDisplayLength(trimmed, nameBudget);
  return sliced ? `${sliced}${suffix}` : sliceByDisplayLength(trimmed, max);
}

/**
 * 名前から ID が作れない（日本語のみ等）ときは、メールのローカル部を使う。
 * 手入力の追従ロジックは変えない。初期値専用。
 */
export function suggestWorkspaceId(
  displayName: string | null | undefined,
  email?: string | null,
): string {
  const fromName = sanitizeWorkspaceId(displayName || '');
  if (fromName.length >= MIN_WORKSPACE_ID_LENGTH) {
    return fromName.slice(0, MAX_WORKSPACE_ID_LENGTH);
  }
  const local = (email || '').split('@')[0] || '';
  const fromEmail = sanitizeWorkspaceId(local);
  if (fromEmail.length >= MIN_WORKSPACE_ID_LENGTH) {
    return fromEmail.slice(0, MAX_WORKSPACE_ID_LENGTH);
  }
  return (fromEmail || fromName).slice(0, MAX_WORKSPACE_ID_LENGTH);
}

export function validateEmail(email: string): { valid: boolean; error?: string } {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!email.trim()) {
    return { valid: false, error: uiText("Enter your email address.") };
  }
  if (!emailRegex.test(email)) {
    return { valid: false, error: uiText("Enter a valid email address.") };
  }
  return { valid: true };
}

export function validatePassword(password: string): { valid: boolean; error?: string } {
  if (password.length < 8) {
    return { valid: false, error: uiText("Enter a password of at least 8 characters.") };
  }

  let typeCount = 0;
  if (/\d/.test(password)) typeCount++;
  if (/[a-z]/.test(password)) typeCount++;
  if (/[A-Z]/.test(password)) typeCount++;
  if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) typeCount++;

  if (typeCount < 3) {
    return {
      valid: false,
      error: uiText("Use at least 3 of the following: numbers, lowercase, uppercase, and symbols."),
    };
  }
  return { valid: true };
}

export function validateDisplayName(displayName: string): { valid: boolean; error?: string } {
  const trimmed = displayName.trim();
  if (!trimmed) {
    return { valid: false, error: uiText("Enter an account name.") };
  }
  if (trimmed.length < MIN_DISPLAY_NAME_LENGTH) {
    return { valid: false, error: uiText("Enter an account name of at least 2 characters.") };
  }
  if (trimmed.length > MAX_DISPLAY_NAME_LENGTH) {
    return { valid: false, error: uiText("Enter an account name of 20 characters or fewer.") };
  }
  return { valid: true };
}

export function validateWorkspaceName(name: string): { valid: boolean; error?: string } {
  const trimmed = name.trim();
  if (!trimmed) {
    return { valid: false, error: uiText("Please enter a workspace name.") };
  }
  if (getDisplayLength(trimmed) > MAX_WORKSPACE_NAME_DISPLAY_LENGTH) {
    return { valid: false, error: uiText("The workspace name is too long.") };
  }
  return { valid: true };
}

export function validateWorkspaceId(workspaceId: string): { valid: boolean; error?: string } {
  if (!workspaceId) {
    return { valid: false, error: uiText("Please enter a workspace ID.") };
  }
  if (workspaceId !== sanitizeWorkspaceId(workspaceId)) {
    return { valid: false, error: uiText("Workspace IDs can contain only lowercase letters and numbers.") };
  }
  if (workspaceId.length < MIN_WORKSPACE_ID_LENGTH) {
    return { valid: false, error: uiText("Enter a workspace ID of at least {{MIN_WORKSPACE_ID_LENGTH}} characters.", { MIN_WORKSPACE_ID_LENGTH: MIN_WORKSPACE_ID_LENGTH }) };
  }
  if (workspaceId.length > MAX_WORKSPACE_ID_LENGTH) {
    return { valid: false, error: uiText("Enter a workspace ID of {{MAX_WORKSPACE_ID_LENGTH}} characters or fewer.", { MAX_WORKSPACE_ID_LENGTH: MAX_WORKSPACE_ID_LENGTH }) };
  }
  if (RESERVED_WORKSPACE_IDS.includes(workspaceId)) {
    return { valid: false, error: uiText("This workspace ID cannot be used.") };
  }
  return { valid: true };
}

export function validatePublishId(publishId: string): { valid: boolean; error?: string } {
  if (!/^[a-zA-Z0-9-]{3,50}$/.test(publishId)) {
    return {
      valid: false,
      error: uiText("Use only letters, numbers, and hyphens (-), 3–50 characters."),
    };
  }
  if (RESERVED_PUBLISH_IDS.includes(publishId.toLowerCase())) {
    return { valid: false, error: uiText("This public ID cannot be used.") };
  }
  return { valid: true };
}

export function setNcgApplyPending(pending: boolean): void {
  try {
    if (pending) {
      sessionStorage.setItem(NCG_APPLY_PENDING_KEY, '1');
    } else {
      sessionStorage.removeItem(NCG_APPLY_PENDING_KEY);
    }
  } catch {
    // ignore
  }
}

export function hasNcgApplyPending(): boolean {
  try {
    return sessionStorage.getItem(NCG_APPLY_PENDING_KEY) === '1';
  } catch {
    return false;
  }
}

export function provisionErrorMessage(error: unknown): string {
  const code = typeof error === 'object' && error && 'code' in error
    ? String((error as { code?: string }).code)
    : '';
  const message = typeof error === 'object' && error && 'message' in error
    ? String((error as { message?: string }).message)
    : '';

  if (code === 'functions/already-exists' || message.includes('already-exists')) {
    if (message.includes('publish-id-taken')) {
      return uiText("This public ID is already in use. Enter a different ID.");
    }
    return uiText("This workspace ID is already in use. Enter a different ID.");
  }
  if (code === 'functions/failed-precondition' || message.includes('already-has-workspace')) {
    return uiText("You already have a workspace. Sign in and open it from Home.");
  }
  if (code === 'functions/resource-exhausted') {
    return uiText("Too many requests. Please wait a moment and try again.");
  }
  if (code === 'functions/unauthenticated') {
    return uiText("Login required.");
  }
  return uiText("Failed to prepare the workspace. Please try again.");
}
